import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Grid, List, Package, CheckCircle, XCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { BackgroundImage } from '../components/BackgroundImage'
import { CustomPopup, usePopup } from '../components/CustomPopup'
import { authService } from '../services/auth'
import { fetchAuthSession } from 'aws-amplify/auth'
import { SOLUTION_URLS } from '../utils/solutionUrls'
import { useRevenueTracking } from '../hooks/useRevenueTracking'

interface Solution {
  solutionId: string
  name: string
  description: string
  category: string
  pricing: {
    model: string
    amount: number
    currency: string
  }
  partnerName?: string
  status?: string
  actionButtonText?: string
  externalUrl?: string
}

export function CatalogPage() {
  const { user, isAuthenticated } = useAuth()
  const { popup, showPopup, closePopup } = usePopup()
  const navigate = useNavigate()
  const { trackEvent } = useRevenueTracking()
  
  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [solutions, setSolutions] = useState<Solution[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null)
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([])

  const isAdmin = user?.role === 'admin'

  // Get unique categories from solutions
  const categories = ['all', ...new Set(solutions.map(s => s.category))]

  // Filter solutions
  const filteredSolutions = solutions.filter(solution => {
    const matchesSearch = searchQuery === '' || 
      solution.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      solution.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      solution.category.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCategory = selectedCategory === 'all' || solution.category === selectedCategory
    
    const matchesStatus = selectedStatus === 'all' || 
      (selectedStatus === 'live' && (solution as any).solutionStatus === 'live') ||
      (selectedStatus === 'coming-soon' && (solution as any).solutionStatus === 'coming-soon')
    
    return matchesSearch && matchesCategory && matchesStatus
  })

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedCategory('all')
    setSelectedStatus('all')
  }

  const hasActiveFilters = searchQuery !== '' || selectedCategory !== 'all' || selectedStatus !== 'all'

  // Get user's subscription for a solution
  const getUserSubscription = (solutionId: string) => {
    return userSubscriptions.find(sub => sub.solutionId === solutionId && sub.status === 'active')
  }

  useEffect(() => {
    loadSolutions()
  }, [])

  // Fetch user subscriptions
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      fetchUserSubscriptions()
    }
  }, [isAuthenticated, user])

  const loadSolutions = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Customers see approved solutions, admin sees all solutions
      const endpoint = isAdmin ? '/admin/solutions' : '/catalog'
      const apiUrl = (import.meta as any).env.VITE_API_URL || 'https://1d98dlxxhh.execute-api.us-east-1.amazonaws.com/prod'
      
      const headers: any = {}
      
      // If user is logged in, they must have a valid session - use a dummy token for API calls
      if (user && user.email) {
        headers['Authorization'] = `Bearer dummy-token-for-logged-in-user`
        console.log('🔍 Debug - User is logged in, using dummy auth header')
      } else {
        console.log('🔍 Debug - No user found')
      }
      
      console.log('Loading solutions from:', `${apiUrl}${endpoint}`, 'with auth:', !!headers.Authorization)
      
      const response = await fetch(`${apiUrl.replace(/\/$/, '')}${endpoint}`, {
        headers
      })
      
      console.log('Response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('Solutions data:', data)
        
        // Add externalUrl to solutions that have URLs in SOLUTION_URLS
        const solutionsWithUrls = (data.solutions || []).map((solution: Solution) => ({
          ...solution,
          externalUrl: SOLUTION_URLS[solution.solutionId] || undefined
        }))
        
        setSolutions(solutionsWithUrls)
      } else {
        const errorText = await response.text()
        console.error('API Error:', response.status, errorText)
        setError(`Failed to load solutions: ${response.status}`)
      }
    } catch (error) {
      console.error('Network error:', error)
      setError('Network error loading solutions')
    } finally {
      setLoading(false)
    }
  }

  const fetchUserSubscriptions = async () => {
    try {
      const userEmail = user?.email || localStorage.getItem('userEmail') || localStorage.getItem('email')
      if (!userEmail) return

      const response = await fetch(`${(import.meta.env.VITE_API_URL as string).replace(/\/$/, '')}/api/subscriptions`, {
        headers: { 
          'x-user-email': userEmail
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setUserSubscriptions(data.subscriptions || [])
      }
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err)
    }
  }

  const handleSolutionAction = async (solutionId: string, action: 'approve' | 'reject') => {
    try {
      let token = authService.getToken()
      if (!token) {
        try {
          const session = await fetchAuthSession()
          token = session.tokens?.accessToken?.toString() || null
          if (token) {
            authService.setToken(token)
          }
        } catch (error) {
          console.error('Failed to get session token:', error)
        }
      }
      
      if (!token) {
        console.error('No auth token found')
        return
      }
      
      const response = await fetch(`${(import.meta as any).env.VITE_API_URL.replace(/\/$/, '')}/admin/solutions/${solutionId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action })
      })
      
      if (response.ok) {
        loadSolutions()
        setSelectedSolution(null)
      } else {
        console.error('Failed to update solution:', response.status, await response.text())
      }
    } catch (error) {
      console.error('Failed to update solution:', error)
    }
  }

  const handlePurchase = async (solution: Solution | null) => {
    if (!solution || !user) return
    
    try {
      const token = authService.getToken()
      if (!token) {
        console.error('No auth token found')
        return
      }

      const response = await fetch(`${(import.meta as any).env.VITE_API_URL.replace(/\/$/, '')}/payments/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          solutionId: solution.solutionId,
          amount: solution.pricing.amount,
          currency: solution.pricing.currency,
          userId: user.userId,
          userEmail: user.email,
          userName: user.profile.name
        })
      })

      const data = await response.json()
      
      if (data.success && data.redirectUrl) {
        // Redirect to PhonePe payment page
        window.location.href = data.redirectUrl
      } else {
        console.error('Payment initiation failed:', data.message)
        showPopup('Payment initiation failed. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Payment error:', error)
      showPopup('Payment initiation failed. Please try again.', 'error')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen relative">
        <BackgroundImage />
        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading solutions...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative">
      <BackgroundImage />
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Solution Catalog</h1>
          {user?.role === 'partner' && user?.marketplaceStatus === 'active' && (
            <button
              onClick={() => window.location.href = '/partner/solutions/add'}
              className="btn-primary flex items-center space-x-2"
            >
              <span>Add Solution</span>
            </button>
          )}
        </div>
        <p className="text-gray-600">
          Discover software solutions from our trusted partners
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col gap-4">
          {/* Search and View Toggle Row */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search solutions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    trackEvent('search_performed', { query: searchQuery, resultsCount: filteredSolutions.length })
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 border rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-50 border-blue-500 text-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 border rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-50 border-blue-500 text-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="live">Live Only</option>
              <option value="coming-soon">Early Access</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filters
              </button>
            )}

            <div className="sm:ml-auto flex items-center text-sm text-gray-600">
              Showing <span className="font-semibold mx-1">{filteredSolutions.length}</span> of <span className="font-semibold mx-1">{solutions.length}</span> solutions
            </div>
          </div>
        </div>
      </div>

      {/* Solutions Grid */}
      {error ? (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Solutions</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={loadSolutions}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : solutions.length === 0 ? (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Solutions Available</h2>
            <p className="text-gray-600 mb-6">
              There are currently no approved solutions in the catalog. Check back soon!
            </p>
          </div>
        </div>
      ) : filteredSolutions.length === 0 ? (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Solutions Found</h2>
            <p className="text-gray-600 mb-6">
              Try adjusting your search or filters to find what you're looking for.
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
          {filteredSolutions.map((solution) => {
            const proPrice = (solution.pricing as any)?.pro?.displayPrice || (solution.pricing as any)?.pro?.M?.displayPrice?.N || (solution.pricing as any)?.pro?.price || (solution.pricing as any)?.pro?.M?.price?.N
            const hasFree = (solution.pricing as any)?.free || (solution.pricing as any)?.free?.M
            const userSub = getUserSubscription(solution.solutionId)
            
            return (
            <div key={solution.solutionId} className={`bg-white rounded-lg shadow-md border-2 border-gray-200 overflow-hidden hover:shadow-xl hover:border-blue-300 transition-all ${viewMode === 'list' ? 'flex flex-row' : ''}`}>
              {/* Section 1: Identity & Description */}
              <div className={viewMode === 'list' ? 'flex-1 p-4' : 'p-4'}>
                <div className="flex items-start justify-between mb-2">
                  <h3 
                    onClick={() => navigate(`/solutions/${solution.solutionId}`)}
                    className="text-lg font-bold text-blue-600 leading-tight flex-1 cursor-pointer hover:text-blue-700 underline decoration-2 underline-offset-2 transition-colors flex items-center gap-2"
                  >
                    {(solution as any).icon && <span className="text-2xl">{(solution as any).icon}</span>}
                    {solution.name}
                    <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {/* User Subscription Badge */}
                    {userSub && (
                      userSub.planType === 'pro' ? (
                        <span className="px-2 py-1 text-xs rounded-full font-semibold border bg-green-100 text-green-800 border-green-300">
                          ✓ Your Plan: <span className="text-green-900">Pro</span>
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full font-semibold border bg-gray-100 text-gray-700 border-gray-300">
                          Your Plan: <span className="text-blue-600 font-bold">Free</span>
                        </span>
                      )
                    )}
                    {(solution as any).solutionStatus === 'coming-soon' && (
                      <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 font-semibold">
                        Early Access
                      </span>
                    )}
                    {(solution as any).solutionStatus === 'beta' && (
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 font-semibold">
                        Beta
                      </span>
                    )}
                    {(solution as any).currentVersion && (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 font-mono">
                        v{(solution as any).currentVersion}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Compact vendor & category line */}
                <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
                  <span>Sold by {solution.partnerName || 'CloudNestle'}</span>
                  <span className="text-gray-400">•</span>
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{solution.category}</span>
                </div>
                
                <p className="text-sm text-gray-600 line-clamp-2">{solution.description}</p>
              </div>
              
              {/* Section 2: Plans & Pricing */}
              <div className={`px-4 py-3 bg-gray-50 border-t border-gray-200 ${viewMode === 'list' ? 'w-64 border-l flex-shrink-0' : ''}`}>
                <div className="space-y-2">
                  {/* Available Plans */}
                  <div className={`flex ${viewMode === 'list' ? 'flex-col' : 'items-center'} gap-2 text-sm`}>
                    {hasFree && <span className="text-green-600 font-medium">✓ Free Tier Available</span>}
                    {hasFree && proPrice && viewMode === 'grid' && <span className="text-gray-400">|</span>}
                    {proPrice && (
                      <div className="flex flex-col">
                        <span className="text-purple-700 font-medium">Pro ${proPrice}/user/mo*</span>
                        <span className="text-xs text-gray-500">*In your currency</span>
                      </div>
                    )}
                  </div>
                  
                </div>
              </div>
            </div>
            )
          })}
        </div>
      )}

      {/* Solution Detail Modal */}
      {selectedSolution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{selectedSolution.name}</h2>
                <button 
                  onClick={() => {
                    console.log('Closing modal')
                    setSelectedSolution(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Category</h3>
                  <p className="text-gray-700">{selectedSolution.category}</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700">{selectedSolution.description}</p>
                </div>
                
                {selectedSolution.pricing.amount > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Pricing</h3>
                    <p className="text-gray-700">
                      {selectedSolution.pricing.currency} {selectedSolution.pricing.amount}
                      {selectedSolution.pricing.model === 'subscription' && ' per month'}
                      {selectedSolution.pricing.model === 'upfront' && ' one-time'}
                    </p>
                  </div>
                )}
                
                {selectedSolution.partnerName && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Partner</h3>
                    <p className="text-gray-700">{selectedSolution.partnerName}</p>
                  </div>
                )}
                
                {isAdmin && selectedSolution.status && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Status</h3>
                    <span className={`px-3 py-1 text-sm rounded-full ${
                      selectedSolution.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedSolution.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedSolution.status}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                {isAdmin && selectedSolution.status === 'pending' ? (
                  <>
                    <button
                      onClick={() => handleSolutionAction(selectedSolution.solutionId, 'approve')}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleSolutionAction(selectedSolution.solutionId, 'reject')}
                      className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                  </>
                ) : user?.role === 'customer' ? (
                  <button 
                    className="btn-primary"
                    onClick={() => handlePurchase(selectedSolution)}
                  >
                    Purchase Solution
                  </button>
                ) : (
                  <button className="btn-outline" disabled>
                    {user?.role === 'partner' ? 'Partners cannot purchase' : 
                     user?.role === 'admin' ? 'Admins cannot purchase' : 
                     'Login to purchase'}
                  </button>
                )}
                <button 
                  onClick={() => setSelectedSolution(null)}
                  className="btn-outline"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <CustomPopup
        message={popup.message}
        type={popup.type}
        isOpen={popup.isOpen}
        onClose={closePopup}
      />
      </div>
    </div>
  )
}